import React, { ReactNode } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spin } from 'antd';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute component that requires authentication.
 * Redirects to /signin with redirect param when not authenticated,
 * so user always lands on a known page (never blank).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state (including token refresh attempt)
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to sign-in page with current path for post-login redirect
  if (!isAuthenticated) {
    const redirectPath = location.pathname + location.search;
    const signInPath = redirectPath ? `/signin?redirect=${encodeURIComponent(redirectPath)}` : '/signin';
    return <Navigate to={signInPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
